import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycPartyCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { PartyDto } from "@/features/kyc/api/dto";

/**
 * Met à jour une identité (`PUT /Parties/{id}`). Les champs omis restent
 * inchangés côté serveur ; les PII fournis sont re-hashés/re-chiffrés.
 */
export const useUpdateParty = (partyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "parties", "update", partyId],
    mutationFn: (body: Partial<PartyDto>) =>
      mutateData({
        url: kycEndpoints.parties.update(partyId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<PartyDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycPartyCacheKeys(partyId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateParty failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
